import React from "react";
import { motion } from "framer-motion";
import {
  Monitor,
  Server,
  BarChart3,
  FileCheck,
  Shield,
  Bot,
  MessageSquare,
  Image,
  Mic,
  AlertCircle,
  Calculator,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";

const softwareItems = [
  { icon: Monitor, name: "Practice Management System", cost: "$400-700", avg: 550, examples: "Dentrix, Eaglesoft, Open Dental" },
  { icon: Server, name: "IT Spend for PMS", cost: "$200-400", avg: 300, examples: "Servers, maintenance, backups" },
  { icon: BarChart3, name: "Dental Intel", cost: "$300-500", avg: 400, examples: "Revenue intelligence & analytics" },
  { icon: FileCheck, name: "eClaims Portals", cost: "$150-250", avg: 200, examples: "Vyne Trellis, DentalXChange" },
  { icon: Shield, name: "Insurance Verification", cost: "$200-400", avg: 300, examples: "Zuub, AirPay, Vyne" },
  { icon: Bot, name: "AI Receptionist", cost: "$300-500", avg: 400, examples: "Dentina, Arini" },
  { icon: MessageSquare, name: "Communication Suite", cost: "$700-1,200", avg: 950, examples: "Weave, Mango, Podium" },
  { icon: Image, name: "Imaging Software", cost: "$200-400", avg: 300, examples: "Dexis, Apteryx, Carestream" },
  { icon: Mic, name: "AI Scribe ($149/mo x2 providers)", cost: "$298", avg: 298, examples: "Typically 2 providers per office" },
];

const totalAvg = softwareItems.reduce((acc, item) => acc + item.avg, 0);

const SoftwareStackCostSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 lg:p-8 relative overflow-hidden font-sans">
      {/* Background */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-100 rounded-full blur-[120px] opacity-40 translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-50 rounded-full blur-[100px] opacity-40 -translate-x-1/2 translate-y-1/2" />

      <div className="relative z-10 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-start justify-between mb-4 flex-shrink-0">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold mb-2 border border-blue-100">
              <Calculator className="w-4 h-4" />
              <span>Software Consolidation</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Asha's <span className="text-blue-600">software</span> replaces & consolidates 8-10 <span className="text-blue-600">softwares</span> you pay for today
            </h2>
            <p className="text-base font-medium text-slate-700 leading-relaxed mt-3">
              Even after paying for all these tools, they're disjoint platforms that don't stitch together. Data lives in silos, workflows break between systems, and your staff spends time manually bridging the gaps.
            </p>
          </motion.div>

          {/* Total card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl border border-red-100 shadow-lg p-5 transform rotate-1"
          >
            <span className="text-xs font-bold text-red-400 uppercase tracking-wider block mb-1">
              Total Monthly Software Spend
            </span>
            <div className="flex items-baseline gap-1">
              <span className="text-3xl font-extrabold text-red-500 tracking-tight tabular-nums">
                ~${totalAvg.toLocaleString()}
              </span>
              <span className="text-sm text-red-300 font-medium">/mo</span>
            </div>
            <span className="text-[10px] text-slate-400">Based on typical mid-range pricing</span>
          </motion.div>
        </div>

        {/* Table */}
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="flex-1 bg-white rounded-3xl border border-slate-200/60 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] overflow-hidden mb-4"
        >
          <Table>
            <TableHeader className="bg-slate-50 sticky top-0">
              <TableRow className="border-b border-slate-200/80">
                <TableHead className="text-xs uppercase tracking-widest font-bold text-slate-400 py-3 px-5 w-[45%]">
                  Software
                </TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-slate-400 py-3 px-5 w-[35%]">
                  Common Vendors
                </TableHead>
                <TableHead className="text-xs uppercase tracking-widest font-bold text-slate-400 py-3 px-5 w-[20%] text-right">
                  Typical Cost
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {softwareItems.map((item, i) => {
                const Icon = item.icon;
                return (
                  <TableRow
                    key={i}
                    className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors"
                  >
                    <TableCell className="py-2.5 px-5">
                      <div className="flex items-center gap-3">
                        <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
                        <span className="text-sm font-semibold text-slate-800">
                          {item.name}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-2.5 px-5 text-sm text-slate-400">
                      {item.examples}
                    </TableCell>
                    <TableCell className="py-2.5 px-5 text-right">
                      <span className="text-sm font-bold text-red-500">
                        {item.cost}
                        <span className="text-xs font-medium text-slate-400">/mo</span>
                      </span>
                    </TableCell>
                  </TableRow>
                );
              })}
              {/* Total Row */}
              <TableRow className="bg-gradient-to-r from-transparent via-red-50/30 to-transparent shadow-sm">
                <TableCell className="py-3 px-5">
                  <span className="text-base font-extrabold text-slate-900 tracking-wide">
                    Total Monthly Cost
                  </span>
                </TableCell>
                <TableCell className="py-3 px-5 text-sm text-slate-400">
                  Across 8-10 separate vendors
                </TableCell>
                <TableCell className="py-3 px-5 text-right">
                  <span className="text-base font-extrabold text-red-500">
                    ~${totalAvg.toLocaleString()}
                    <span className="text-xs font-medium text-red-300">/mo</span>
                  </span>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </motion.div>

      </div>
    </div>
  );
};

export default SoftwareStackCostSlide;
